import { useState } from "react";
import { Download, Copy } from "lucide-react";
import { toast } from "sonner";
import AavaaLogo from "@/components/AavaaLogo";

/* ============================================================
   Logo SVG strings (inline so they can be downloaded).
   Each is self-contained (no external refs).
   ============================================================ */

const SVG_MARK_COLOR = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="512" height="512">
  <defs>
    <linearGradient id="body" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#FDFBF6"/>
      <stop offset="100%" stop-color="#F3EFE6"/>
    </linearGradient>
    <linearGradient id="sage" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#A7B399"/>
      <stop offset="100%" stop-color="#7A8A6E"/>
    </linearGradient>
    <linearGradient id="warm" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#E7C19A"/>
      <stop offset="100%" stop-color="#D4A373"/>
    </linearGradient>
  </defs>
  <rect x="0" y="0" width="64" height="64" rx="18" fill="url(#body)" stroke="#E2DFD8" stroke-width="1"/>
  <circle cx="24" cy="32" r="13" fill="url(#sage)"/>
  <circle cx="40" cy="32" r="13" fill="url(#warm)"/>
  <path d="M 32 21 a 13 13 0 0 1 0 22 a 13 13 0 0 1 0 -22 Z" fill="#FDFBF6" opacity="0.28"/>
  <circle cx="24" cy="32" r="2.2" fill="#3D4A3F"/>
  <circle cx="40" cy="32" r="2.2" fill="#3D4A3F"/>
</svg>`;

const SVG_MARK_DARK = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="512" height="512">
  <rect x="0" y="0" width="64" height="64" rx="18" fill="#3D4A3F"/>
  <circle cx="24" cy="32" r="13" fill="#A7B399"/>
  <circle cx="40" cy="32" r="13" fill="#D4A373"/>
  <path d="M 32 21 a 13 13 0 0 1 0 22 a 13 13 0 0 1 0 -22 Z" fill="#3D4A3F" opacity="0.5"/>
  <circle cx="24" cy="32" r="2.2" fill="#3D4A3F"/>
  <circle cx="40" cy="32" r="2.2" fill="#3D4A3F"/>
</svg>`;

const SVG_MARK_MONO = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64" width="512" height="512">
  <rect x="0" y="0" width="64" height="64" rx="18" fill="#3D4A3F"/>
  <circle cx="24" cy="32" r="13" fill="#FAFAF7"/>
  <circle cx="40" cy="32" r="13" fill="#FAFAF7"/>
  <path d="M 32 21 a 13 13 0 0 1 0 22 a 13 13 0 0 1 0 -22 Z" fill="#3D4A3F" opacity="0.55"/>
  <circle cx="24" cy="32" r="2.2" fill="#3D4A3F"/>
  <circle cx="40" cy="32" r="2.2" fill="#3D4A3F"/>
</svg>`;

const SVG_WORDMARK_COLOR = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 300 80" width="900" height="240">
  <defs>
    <linearGradient id="body" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#FDFBF6"/>
      <stop offset="100%" stop-color="#F3EFE6"/>
    </linearGradient>
    <linearGradient id="sage" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#A7B399"/>
      <stop offset="100%" stop-color="#7A8A6E"/>
    </linearGradient>
    <linearGradient id="warm" x1="0%" y1="0%" x2="100%" y2="100%">
      <stop offset="0%" stop-color="#E7C19A"/>
      <stop offset="100%" stop-color="#D4A373"/>
    </linearGradient>
  </defs>
  <g transform="translate(10,10)">
    <rect x="0" y="0" width="60" height="60" rx="17" fill="url(#body)" stroke="#E2DFD8" stroke-width="1"/>
    <circle cx="22.5" cy="30" r="12" fill="url(#sage)"/>
    <circle cx="37.5" cy="30" r="12" fill="url(#warm)"/>
    <path d="M 30 20 a 12 12 0 0 1 0 20 a 12 12 0 0 1 0 -20 Z" fill="#FDFBF6" opacity="0.28"/>
    <circle cx="22.5" cy="30" r="2" fill="#3D4A3F"/>
    <circle cx="37.5" cy="30" r="2" fill="#3D4A3F"/>
  </g>
  <text x="85" y="52" font-family="'Outfit', sans-serif" font-size="40" font-weight="600" fill="#3D4A3F" letter-spacing="-0.5">aavaa</text>
</svg>`;

/* ============================================================
   Helpers
   ============================================================ */

function downloadSvg(svgString, filename) {
  const blob = new Blob([svgString], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadPng(svgString, filename, size = 1024) {
  const img = new Image();
  // SVG has viewBox but may have width/height attrs — we'll draw at target size
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, size, size);
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };
  img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgString)));
}

function downloadPngCustom(svgString, filename, width, height) {
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, width, height);
    canvas.toBlob((blob) => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };
  img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgString)));
}

/* ============================================================
   Brand-kit page
   ============================================================ */

export default function BrandKit() {
  const copyHex = (hex) => {
    navigator.clipboard.writeText(hex);
    toast.success(`Copied ${hex}`);
  };

  const tiles = [
    {
      title: "Primary logo",
      subtitle: "Full-color on warm cream — use everywhere by default.",
      bg: "#FAFAF7",
      svg: SVG_MARK_COLOR,
      file: "aavaa-logo-primary",
    },
    {
      title: "Dark / on-dark",
      subtitle: "Forest-ink background for Instagram stories, dark themes.",
      bg: "#3D4A3F",
      svg: SVG_MARK_DARK,
      file: "aavaa-logo-dark",
    },
    {
      title: "Monochrome",
      subtitle: "Single-color — best for small surfaces, stamps, watermarks.",
      bg: "#3D4A3F",
      svg: SVG_MARK_MONO,
      file: "aavaa-logo-mono",
    },
  ];

  return (
    <div className="min-h-screen bg-[#1F2420] text-white p-6 sm:p-10" data-testid="brand-kit-page">
      <div className="max-w-5xl mx-auto">
        <header className="flex items-center justify-between mb-8">
          <div>
            <p className="font-manrope text-xs tracking-[0.15em] text-[#D4A373] uppercase mb-1">Brand kit</p>
            <h1 className="font-outfit text-3xl font-medium tracking-tight">Aavaa logo — download</h1>
            <p className="font-manrope text-sm text-white/60 mt-1">
              SVG for crispness at any size. PNG for quick drops into slides / social.
            </p>
          </div>
          <AavaaLogo variant="wordmark" size={42} />
        </header>

        <div className="grid md:grid-cols-3 gap-5 mb-10">
          {tiles.map((t) => (
            <div key={t.file} className="bg-[#2A302B] rounded-2xl overflow-hidden border border-white/5">
              <div className="p-8 flex items-center justify-center" style={{ background: t.bg }}>
                <div dangerouslySetInnerHTML={{ __html: t.svg.replace('width="512"', 'width="160"').replace('height="512"', 'height="160"') }} />
              </div>
              <div className="p-5">
                <h3 className="font-outfit text-lg font-medium mb-1">{t.title}</h3>
                <p className="font-manrope text-xs text-white/55 leading-relaxed mb-4">{t.subtitle}</p>
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid={`${t.file}-svg`}
                    onClick={() => downloadSvg(t.svg, `${t.file}.svg`)}
                    className="flex items-center gap-1.5 text-xs font-outfit rounded-full bg-white text-[#1F2420] px-3 py-1.5 hover:bg-white/90 transition-colors"
                  >
                    <Download size={12} /> SVG
                  </button>
                  <button
                    data-testid={`${t.file}-png-512`}
                    onClick={() => downloadPng(t.svg, `${t.file}-512.png`, 512)}
                    className="flex items-center gap-1.5 text-xs font-outfit rounded-full bg-[#8E9B82] text-white px-3 py-1.5 hover:bg-[#7A8A6E] transition-colors"
                  >
                    <Download size={12} /> PNG 512
                  </button>
                  <button
                    data-testid={`${t.file}-png-1024`}
                    onClick={() => downloadPng(t.svg, `${t.file}-1024.png`, 1024)}
                    className="flex items-center gap-1.5 text-xs font-outfit rounded-full bg-[#8E9B82] text-white px-3 py-1.5 hover:bg-[#7A8A6E] transition-colors"
                  >
                    <Download size={12} /> PNG 1024
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Wordmark */}
        <div className="bg-[#2A302B] rounded-2xl overflow-hidden border border-white/5 mb-10">
          <div className="p-10 flex items-center justify-center" style={{ background: "#FAFAF7" }}>
            <div dangerouslySetInnerHTML={{ __html: SVG_WORDMARK_COLOR.replace('width="900"', 'width="360"').replace('height="240"', 'height="96"') }} />
          </div>
          <div className="p-5 flex flex-wrap items-center justify-between gap-4">
            <div>
              <h3 className="font-outfit text-lg font-medium">Wordmark</h3>
              <p className="font-manrope text-xs text-white/55 leading-relaxed">Mark + "aavaa" word — for nav, footer, email signature.</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="wordmark-svg"
                onClick={() => downloadSvg(SVG_WORDMARK_COLOR, "aavaa-wordmark.svg")}
                className="flex items-center gap-1.5 text-xs font-outfit rounded-full bg-white text-[#1F2420] px-3 py-1.5 hover:bg-white/90 transition-colors"
              >
                <Download size={12} /> SVG
              </button>
              <button
                data-testid="wordmark-png"
                onClick={() => downloadPngCustom(SVG_WORDMARK_COLOR, "aavaa-wordmark-1200.png", 1200, 320)}
                className="flex items-center gap-1.5 text-xs font-outfit rounded-full bg-[#8E9B82] text-white px-3 py-1.5 hover:bg-[#7A8A6E] transition-colors"
              >
                <Download size={12} /> PNG 1200×320
              </button>
            </div>
          </div>
        </div>

        {/* Color palette */}
        <h2 className="font-outfit text-xl font-medium mb-4">Brand colors</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3 mb-6">
          {[
            { name: "Ink", hex: "#3D4A3F" },
            { name: "Sage", hex: "#8E9B82" },
            { name: "Warm", hex: "#D4A373" },
            { name: "Cream", hex: "#F7F2E8" },
            { name: "Stone", hex: "#E2DFD8" },
            { name: "Subtle", hex: "#6B7264" },
          ].map((c) => (
            <button
              key={c.hex}
              data-testid={`color-${c.name.toLowerCase()}`}
              onClick={() => copyHex(c.hex)}
              className="bg-[#2A302B] rounded-xl border border-white/5 overflow-hidden text-left hover:border-white/20 transition-colors"
            >
              <div className="h-16" style={{ background: c.hex }} />
              <div className="p-3 flex items-center justify-between">
                <div>
                  <p className="font-outfit text-sm font-medium">{c.name}</p>
                  <p className="font-mono text-[10px] text-white/55">{c.hex}</p>
                </div>
                <Copy size={12} className="text-white/40" />
              </div>
            </button>
          ))}
        </div>

        <p className="font-manrope text-xs text-white/40 mt-8">
          Tip: SVG is infinitely scalable and perfect for print, favicon, and websites. PNG is handy for quick shares on Instagram, Slack, WhatsApp.
        </p>
      </div>
    </div>
  );
}
